'use client';

import React, { useState, useRef, useEffect } from 'react';

interface WorkLink {
  label: string;
  url: string;
}

interface WorkSubsection {
  title: string;
  description: string;
  strategy: string;
  skills: string[];
  links?: WorkLink[];
}

interface WorkProject {
  id: string;
  number: string;
  category: string;
  company: string;
  title: string;
  description: string;
  strategy: string;
  skills: string[];
  links: WorkLink[];
  accentColor: string;
  bgStyle: 'purple' | 'cyan' | 'dark';
  icon: string;
  subsections?: WorkSubsection[];
}

const selectedWork: WorkProject[] = [
  {
    id: 'nightborn-content',
    number: '01',
    category: 'Content Strategy & Digital Storytelling',
    company: 'Nightborn Agency — Brussels',
    title: 'Brand Positioning Through Content',
    description:
      'At Nightborn, a Brussels-based digital product agency, I led content strategy and digital storytelling initiatives that supported brand positioning and product communication across client portfolios. Working across case studies, editorial content, and thought leadership, I translated complex technical narratives into compelling stories that resonated with both B2B decision-makers and end users.',
    strategy:
      'Developed editorial frameworks that aligned product messaging with audience intent — turning app development milestones into brand-building moments. Each piece of content was engineered to reinforce Nightborn\'s authority in digital product development while driving organic discoverability.',
    skills: ['Content Strategy', 'Digital Storytelling', 'Brand Positioning', 'Editorial Planning', 'B2B Communication'],
    links: [
      { label: 'Sandora Project', url: 'https://www.nightborn.com/projects/sandora' },
      { label: 'Karomia Project', url: 'https://www.nightborn.com/projects/karomia' },
      { label: 'Blog: Triple Constraint of App Development', url: 'https://www.nightborn.com/blog/the-triple-constraint-of-app-development-managing-cost-time-and-scope-' },
    ],
    accentColor: '#7c3aed',
    bgStyle: 'purple',
    icon: '✍️',
  },
  {
    id: 'rikolto-editorial',
    number: '02',
    category: 'Editorial & Thought Leadership',
    company: 'Rikolto — Leuven',
    title: 'Storytelling for Impact-Driven Organisations',
    description:
      'For Rikolto, an international NGO working on sustainable food systems across Africa, Asia, and Latin America, I produced long-form editorial content that translated complex field realities into accessible, emotionally resonant stories. The work spanned sustainability communication, farmer empowerment narratives, and food innovation — all crafted to engage global donors, partners, and policy audiences.',
    strategy:
      'My approach centred on evidence-based storytelling: grounding each piece in real data and human experience, then structuring it for maximum impact across digital and print channels. This editorial discipline helped Rikolto strengthen its thought leadership positioning and deepen stakeholder trust.',
    skills: ['Long-Form Content Writing', 'Sustainability Communication', 'Impact Storytelling', 'NGO Communications', 'Audience Engagement'],
    links: [
      { label: 'Nyange Nyange — Congolese Rice Farmers', url: 'https://www.rikolto.org/stories/nyange-nyange-a-local-label-that-facilitates-market-access-for-congolese-rice-farmers' },
      { label: 'GoodFood School — Belgium & Europe', url: 'https://www.rikolto.org/stories/making-goodfood-school-the-norm-in-belgium-and-throughout-europe' },
      { label: 'Kerinci Specialty Coffee Innovation', url: 'https://www.rikolto.org/stories/not-your-average-coffee-innovation-and-sustainable-practices-for-high-quality-kerinci-specialty-coffee' },
    ],
    accentColor: '#0ECAD4',
    bgStyle: 'cyan',
    icon: '🌍',
  },
  {
    id: 'antwerpen-braider',
    number: '03',
    category: 'SEO & GEO Optimization',
    company: 'Antwerpen Braider',
    title: 'Local Search Dominance Through Strategic SEO',
    description:
      'For Antwerpen Braider, a beauty and lifestyle brand based in Antwerp, I designed and executed a content-led SEO and GEO (Generative Engine Optimization) strategy focused on improving local discoverability and capturing high-intent search traffic. The goal was to make the brand the go-to result for braiding services in Antwerp — both in traditional search and AI-powered discovery tools.',
    strategy:
      'Combined on-page SEO fundamentals (keyword mapping, meta optimization, structured data) with GEO-focused content that answered natural language queries. Built location-specific landing pages and optimised Google Business Profile signals to dominate local pack results and increase qualified foot traffic.',
    skills: ['SEO Strategy', 'GEO Optimization', 'Local SEO', 'Keyword Research', 'On-Page Optimization', 'Structured Data'],
    links: [
      { label: 'Antwerpen Braider Website', url: 'https://antwerpenbraider.com/homepage' },
    ],
    accentColor: '#7c3aed',
    bgStyle: 'dark',
    icon: '📈',
    subsections: [
      {
        title: 'Social Media Management & Content Execution',
        description:
          'Managed Antwerpen Braider\'s Instagram presence end-to-end — from content planning and ideation through to creation, scheduling, and publishing. I oversaw the full content lifecycle, ensuring every post served a strategic purpose: building brand awareness, driving engagement, and reinforcing the brand\'s identity as the go-to braiding specialist in Antwerp.',
        strategy:
          'Maintained strict visual identity consistency across all posts — from colour palette and typography to tone of voice — so that every piece of content felt unmistakably on-brand. Content was planned in advance using a structured editorial calendar, allowing for proactive storytelling rather than reactive posting.',
        skills: ['Instagram Management', 'Content Planning', 'Editorial Calendar', 'Visual Identity', 'Content Scheduling'],
        links: [
          { label: 'Instagram: @antwerpbraider', url: 'https://www.instagram.com/antwerpbraider' },
        ],
      },
      {
        title: 'TikTok Growth & Platform Expansion',
        description:
          'Supported Antwerpen Braider\'s expansion onto TikTok by developing a short-form video content strategy tailored to the platform\'s discovery algorithms and mobile-first audience. The goal was to extend the brand\'s reach beyond Instagram and position it in front of a younger, trend-driven demographic actively searching for braiding inspiration and services.',
        strategy:
          'Adapted existing brand content for TikTok\'s native format — prioritising fast hooks, authentic storytelling, and trend-aligned audio to maximise organic reach. Content was structured to perform in the For You Page (FYP) algorithm, using keyword-rich captions and strategic hashtags to drive discoverability among local and niche audiences.',
        skills: ['TikTok Strategy', 'Short-Form Video', 'Platform Expansion', 'Algorithm Optimisation', 'Mobile-First Content'],
        links: [
          { label: 'TikTok: @antwerpbraider', url: 'https://www.tiktok.com/@antwerpbraider' },
        ],
      },
      {
        title: 'Brand Guidelines & Communication Structure',
        description:
          'Developed a comprehensive set of brand guidelines for Antwerpen Braider to ensure consistent tone, visual identity, and messaging across all digital touchpoints. This included defining content pillars, communication frameworks, and a clear brand voice — giving the brand a structured foundation to scale its presence without losing coherence.',
        strategy:
          'Structured the brand\'s communication around three core pillars: expertise, community, and transformation. Each pillar informed content decisions across social, web, and email — ensuring that every message reinforced the same brand promise. The guidelines served as a practical reference for content creation, making it easier to maintain consistency as the brand grew.',
        skills: ['Brand Guidelines', 'Content Pillars', 'Communication Framework', 'Brand Voice', 'Digital Strategy'],
      },
      {
        title: 'Email Marketing Integration',
        description:
          'Created structured communication flows for Antwerpen Braider\'s email marketing, designed to nurture leads, retain existing clients, and drive repeat bookings. Each flow was built with a clear objective — whether onboarding new subscribers, promoting seasonal offers, or re-engaging dormant contacts.',
        strategy:
          'Ensured full alignment between email, social media, and website messaging so that customers experienced a seamless, consistent brand journey across every touchpoint. Email content was crafted to complement the social calendar — reinforcing key campaigns and driving traffic back to the website — while maintaining the same visual identity and tone established in the brand guidelines.',
        skills: ['Email Marketing', 'Communication Flows', 'Customer Retention', 'Cross-Channel Alignment', 'Brand Consistency'],
      },
    ],
  },
  {
    id: 'rikolto-social',
    number: '04',
    category: 'Social Media Campaign',
    company: 'Rikolto — Instagram',
    title: 'Annual Report Launch Campaign',
    description:
      'I led the social media content strategy for Rikolto\'s Annual Report launch on Instagram — a multi-phase campaign designed to generate anticipation, communicate impact, and drive engagement with a global audience. The campaign used a teaser-to-reveal structure, combining short-form video (Reels) with carousel storytelling to maximise reach and retention.',
    strategy:
      'Each post served a deliberate role in the campaign arc: teasers built curiosity, carousels delivered depth, and the Reel anchored the launch with emotional resonance. Visual communication was engineered to stop the scroll — pairing bold data points with human-centred imagery to make impact feel tangible, not abstract.',
    skills: ['Social Media Strategy', 'Campaign Planning', 'Visual Storytelling', 'Instagram Content', 'Engagement Optimisation'],
    links: [
      { label: 'Campaign Reel', url: 'https://www.instagram.com/reel/CumDu1NocDx/' },
      { label: 'Post 1 — Campaign Teaser', url: 'https://www.instagram.com/p/CtzCQZ5oxqC/' },
      { label: 'Post 2 — Impact Carousel', url: 'https://www.instagram.com/p/CuBqCqCo1hq/' },
      { label: 'Post 3 — Data Story', url: 'https://www.instagram.com/p/CuWesq8oOg6/' },
      { label: 'Post 4 — Launch Reveal', url: 'https://www.instagram.com/p/Cu6gX2_I9S0/' },
    ],
    accentColor: '#0ECAD4',
    bgStyle: 'dark',
    icon: '📱',
  },
  {
    id: 'nightborn-linkedin',
    number: '05',
    category: 'B2B Lead Generation',
    company: 'Nightborn Agency — LinkedIn',
    title: 'Founder-Led Distribution Strategy',
    description:
      'I architected and executed a LinkedIn B2B lead generation campaign for Nightborn Agency using a deliberate founder-led distribution model. Rather than relying solely on the company page, I leveraged the personal LinkedIn accounts of the CEO and COO to amplify content — combining personal reach and brand authority to increase visibility, engagement, and inbound lead flow.',
    strategy:
      'The strategy was rooted in thought leadership positioning: each post was crafted to address a specific pain point in the B2B buyer journey, from pitch inefficiencies to growth stagnation. By publishing through founder profiles, the content achieved significantly higher organic reach than brand-page posts alone — driving qualified conversations and inbound enquiries.',
    skills: ['B2B Lead Generation', 'LinkedIn Strategy', 'Thought Leadership', 'Founder-Led Marketing', 'Inbound Marketing', 'Copywriting'],
    links: [
      { label: 'Pitch Evolution Starts With Mindset Shift', url: 'https://www.linkedin.com/company/nightborn/' },
      { label: 'Forget Disruption — Build Relevance Instead', url: 'https://www.linkedin.com/company/nightborn/' },
      { label: '4 Signs Your Pitch Isn\'t Converting (Yet)', url: 'https://www.linkedin.com/company/nightborn/' },
      { label: 'How AI Fixed What Slowed Down Growth', url: 'https://www.linkedin.com/company/nightborn/' },
      { label: 'Relaunch Strategy: Turning Momentum Into Leads', url: 'https://www.linkedin.com/company/nightborn/' },
    ],
    accentColor: '#7c3aed',
    bgStyle: 'purple',
    icon: '💼',
  },
  {
    id: 'performance-tracking',
    number: '06',
    category: 'Performance Tracking & SEO',
    company: 'Cross-Client',
    title: 'Data-Driven Growth Infrastructure',
    description:
      'Across all client engagements, I built and maintained performance tracking systems that connected content output to measurable business outcomes. From GA4 event configuration to Search Console indexing audits, every campaign was grounded in data — enabling continuous optimisation and clear ROI reporting.',
    strategy:
      'Implemented end-to-end analytics stacks using Google Analytics 4, Search Console, SEMrush, and Ahrefs to monitor traffic, keyword rankings, and indexing health. Managed CMS workflows (WordPress, Webflow) with automation pipelines to streamline publishing and on-page SEO at scale. Redirect management, canonical tag audits, and structured data implementation were standard practice across all projects.',
    skills: ['Google Analytics 4', 'Search Console', 'SEMrush', 'Ahrefs', 'CMS Management', 'Technical SEO', 'Automation Workflows', 'KPI Reporting'],
    links: [],
    accentColor: '#0ECAD4',
    bgStyle: 'cyan',
    icon: '📊',
  },
];

const bgStyles = {
  purple: {
    card: 'bg-[#7c3aed]',
    text: 'text-white',
    subtext: 'text-purple-100',
    tag: 'bg-white/20 text-white border-white/30',
    link: 'text-white hover:text-purple-200',
    linkIcon: 'text-purple-200',
    divider: 'border-white/20',
    categoryText: 'text-purple-200',
    numberBg: 'bg-white/20',
    numberText: 'text-white',
  },
  cyan: {
    card: 'bg-[#0ECAD4]',
    text: 'text-gray-900',
    subtext: 'text-teal-900',
    tag: 'bg-black/15 text-gray-900 border-black/20',
    link: 'text-gray-900 hover:text-teal-800',
    linkIcon: 'text-teal-700',
    divider: 'border-black/15',
    categoryText: 'text-teal-800',
    numberBg: 'bg-black/15',
    numberText: 'text-gray-900',
  },
  dark: {
    card: 'bg-[#0f0f0f] border border-white/10',
    text: 'text-white',
    subtext: 'text-gray-400',
    tag: 'bg-white/10 text-gray-300 border-white/15',
    link: 'text-gray-300 hover:text-white',
    linkIcon: 'text-gray-500',
    divider: 'border-white/10',
    categoryText: 'text-gray-400',
    numberBg: 'bg-white/10',
    numberText: 'text-white',
  },
};

function ExternalLinkIcon({ color }: { color: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="flex-shrink-0">
      <path d="M2 10L10 2M10 2H5M10 2V7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProjectCard({ project, index }: { project: WorkProject; index: number }) {
  const styles = bgStyles[project.bgStyle];
  const accentColor = project.accentColor;

  return (
    <div
      className={`${styles.card} rounded-2xl p-7 md:p-9 flex flex-col gap-6 transition-transform duration-300 hover:-translate-y-1`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className={`${styles.numberBg} ${styles.numberText} font-mono text-xs font-bold px-2.5 py-1 rounded-md`}
          >
            {project.number}
          </span>
          <span className="text-2xl">{project.icon}</span>
        </div>
        <span
          className={`font-condensed text-xs font-bold tracking-widest uppercase ${styles.categoryText}`}
        >
          {project.category}
        </span>
      </div>

      {/* Company + Title */}
      <div>
        <p
          className={`font-condensed text-xs font-bold tracking-widest uppercase mb-2 ${styles.categoryText}`}
        >
          {project.company}
        </p>
        <h3
          className={`font-condensed font-bold uppercase leading-tight ${styles.text}`}
          style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)' }}
        >
          {project.title}
        </h3>
      </div>

      {/* Divider */}
      <div className={`border-t ${styles.divider}`} />

      {/* Description */}
      <p className={`font-sans leading-relaxed text-sm ${styles.subtext}`}>
        {project.description}
      </p>

      {/* Strategy */}
      <div>
        <p
          className={`font-condensed text-xs font-bold tracking-widest uppercase mb-2 ${styles.categoryText}`}
        >
          Strategic Approach
        </p>
        <p className={`font-sans leading-relaxed text-sm ${styles.subtext}`}>
          {project.strategy}
        </p>
      </div>

      {/* Skills */}
      <div className="flex flex-wrap gap-2">
        {project.skills.map((skill) => (
          <span
            key={skill}
            className={`font-condensed text-xs font-bold tracking-wider uppercase px-2.5 py-1 rounded-full border ${styles.tag}`}
          >
            {skill}
          </span>
        ))}
      </div>

      {/* Links */}
      {project.links.length > 0 && (
        <div className={`border-t ${styles.divider} pt-4`}>
          <p
            className={`font-condensed text-xs font-bold tracking-widest uppercase mb-3 ${styles.categoryText}`}
          >
            View Work
          </p>
          <ul className="space-y-2">
            {project.links.map((link) => (
              <li key={link.url}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 font-sans text-sm font-medium transition-all duration-200 group ${styles.link}`}
                >
                  <ExternalLinkIcon color={project.bgStyle === 'cyan' ? '#0f766e' : project.bgStyle === 'purple' ? '#e9d5ff' : '#6b7280'} />
                  <span className="group-hover:underline underline-offset-2">{link.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Subsections */}
      {project.subsections && project.subsections.length > 0 && (
        <div className={`border-t ${styles.divider} pt-6 flex flex-col gap-8`}>
          {project.subsections.map((sub, i) => (
            <div key={i} className="flex flex-col gap-4">
              {/* Subsection divider (except first) */}
              {i > 0 && <div className={`border-t ${styles.divider}`} />}

              {/* Subsection Title */}
              <h4
                className={`font-condensed font-bold uppercase tracking-wide ${styles.text}`}
                style={{ fontSize: 'clamp(0.95rem, 1.8vw, 1.15rem)' }}
              >
                {sub.title}
              </h4>

              {/* Subsection Description */}
              <p className={`font-sans leading-relaxed text-sm ${styles.subtext}`}>
                {sub.description}
              </p>

              {/* Subsection Strategy */}
              <div>
                <p className={`font-condensed text-xs font-bold tracking-widest uppercase mb-2 ${styles.categoryText}`}>
                  Strategic Approach
                </p>
                <p className={`font-sans leading-relaxed text-sm ${styles.subtext}`}>
                  {sub.strategy}
                </p>
              </div>

              {/* Subsection Skills */}
              <div className="flex flex-wrap gap-2">
                {sub.skills.map((skill) => (
                  <span
                    key={skill}
                    className={`font-condensed text-xs font-bold tracking-wider uppercase px-2.5 py-1 rounded-full border ${styles.tag}`}
                  >
                    {skill}
                  </span>
                ))}
              </div>

              {/* Subsection Links */}
              {sub.links && sub.links.length > 0 && (
                <ul className="space-y-2">
                  {sub.links.map((link) => (
                    <li key={link.url}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 font-sans text-sm font-medium transition-all duration-200 group ${styles.link}`}
                      >
                        <ExternalLinkIcon color={project.bgStyle === 'cyan' ? '#0f766e' : project.bgStyle === 'purple' ? '#e9d5ff' : '#6b7280'} />
                        <span className="group-hover:underline underline-offset-2">{link.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Projects() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="projects" className="bg-dark py-24 md:py-32" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-5 md:px-16 lg:px-24">
        {/* Header */}
        <div className="mb-16 md:mb-20">
          <span className="section-label" style={{ color: '#0ECAD4' }}>
            03 — Selected Work
          </span>
          <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="section-title text-light-warm"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 5rem)' }}
            >
              View My
              <br />
              <span style={{ color: '#7c3aed' }}>Work</span>
            </h2>
            <p className="font-sans text-secondary-dark max-w-md leading-relaxed" style={{ opacity: 0.7, fontSize: '0.95rem' }}>
              A curated selection of projects spanning content strategy, SEO, social media campaigns, B2B lead generation, and performance analytics — each built around measurable impact.
            </p>
          </div>
        </div>

        {/* Projects Grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Row 1: Nightborn Content (large) + Rikolto Editorial */}
          <div className="md:col-span-1">
            <ProjectCard project={selectedWork[0]} index={0} />
          </div>
          <div className="md:col-span-1">
            <ProjectCard project={selectedWork[1]} index={1} />
          </div>

          {/* Row 2: Antwerpen Braider (full width) */}
          <div className="md:col-span-2">
            <ProjectCard project={selectedWork[2]} index={2} />
          </div>

          {/* Row 3: Rikolto Social + Nightborn LinkedIn */}
          <div className="md:col-span-1">
            <ProjectCard project={selectedWork[3]} index={3} />
          </div>
          <div className="md:col-span-1">
            <ProjectCard project={selectedWork[4]} index={4} />
          </div>

          {/* Row 4: Performance Tracking (full width) */}
          <div className="md:col-span-2">
            <ProjectCard project={selectedWork[5]} index={5} />
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/10 pt-12">
          <div>
            <p className="font-condensed font-bold uppercase tracking-widest text-xs text-secondary-dark opacity-60 mb-1">
              Ready to collaborate?
            </p>
            <p className="font-sans text-light-warm font-medium">
              Let&apos;s build something that drives real results.
            </p>
          </div>
          <a
            href="#contact"
            className="font-condensed font-bold uppercase tracking-widest text-sm px-8 py-4 text-white transition-all duration-300 hover:opacity-90 hover:scale-105 flex-shrink-0"
            style={{ backgroundColor: '#7c3aed' }}
          >
            Get In Touch →
          </a>
        </div>
      </div>
    </section>
  );
}